import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMiniERPEDI } from './hooks/useMiniERPEDI';
import { useStatusBanner, StatusBanner } from './components/MiniERPStatusBanner';
import { generateRegisterPDF, generateEDIMemoPDF, ORIENTATION } from './components/MiniERPPdf';
import { generateRegisterCSV } from './components/MiniERPCsv';
import { usePDActiveSubscription } from '../purpledoor/hooks/pdActiveSubscription';
import ImageUploader from '../purpledoor/components/ImageUploader';
import MiniERPContactPicker from './components/MiniERPContactPicker';
import MiniERPMyCodeShare from './components/MiniERPMyCodeShare';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faMoneyBillWave, faShoppingCart, faExchangeAlt, faListUl,
  faPlus, faSearch, faLock, faLockOpen, faTimesCircle, faEdit, faFilePdf,
  faSignature, faCheckCircle, faHourglassHalf, faBan, faCheck, faFileCsv
} from '@fortawesome/free-solid-svg-icons';

const CARD_TYPES = [
  { key: 'Sales', label: 'Business Sales', icon: faMoneyBillWave, color: 'emerald', question: 'Which business did you sell to?' },
  { key: 'Purchase', label: 'Business Purchase', icon: faShoppingCart, color: 'rose', question: 'Which business did you buy from?' },
  { key: 'Barter', label: 'Barter', icon: faExchangeAlt, color: 'violet', question: 'Which business are you bartering with?' },
];

const STATUS_STYLE = {
  PendingApproval: { label: 'Pending', color: 'text-amber-400', icon: faHourglassHalf },
  Locked: { label: 'Locked', color: 'text-emerald-400', icon: faLock },
  Rejected: { label: 'Rejected', color: 'text-rose-400', icon: faBan },
};

// A "Purchase" B creates (B is buying) IS a "Sale" from A's side (A is
// selling) — but only one transactionType is stored, from the initiator's
// perspective. Every filter/display that isn't the initiator must flip it.
const viewerType = (t, myUserId) => {
  if (t.transactionType === 'Barter') return 'Barter';
  const iAmInitiator = String(t.initiatorUserId?._id || t.initiatorUserId) === String(myUserId);
  if (iAmInitiator) return t.transactionType;
  return t.transactionType === 'Sales' ? 'Purchase' : 'Sales';
};

const computeRelationshipBalance = (transactions, myUserId) => {
  let theyOweMe = 0, iOweThem = 0;
  transactions.forEach((t) => {
    if (t.status !== 'Locked' || t.isPaid !== false || t.transactionType === 'Barter') return;
    const iAmInitiator = String(t.initiatorUserId?._id || t.initiatorUserId) === String(myUserId);
    if (t.transactionType === 'Sales' && iAmInitiator) theyOweMe += t.amount;
    if (t.transactionType === 'Purchase' && iAmInitiator) iOweThem += t.amount;
    if (t.transactionType === 'Sales' && !iAmInitiator) iOweThem += t.amount;
    if (t.transactionType === 'Purchase' && !iAmInitiator) theyOweMe += t.amount;
  });
  return { theyOweMe, iOweThem, net: theyOweMe - iOweThem };
};

const MiniERPLocalEDI = () => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.authReducer || {});
  const myUserId = authState?.user?._id;
  const myCode = authState?.user?.think5whyscode;
  const { isActive: isSubscribed, loadingSubs } = usePDActiveSubscription('MiniERPEDI');

  const {
    fetchEDITransactions, createEDITransaction, updateEDITransaction,
    lookupBusinessCode, submitOtp, rejectEDITransaction, attachSignature,
    fetchContacts,
  } = useMiniERPEDI();

  const { status, showSuccess, showError } = useStatusBanner();

  // ================= NAVIGATION STATE =================
  const [view, setView] = useState('home'); // 'home' | 'code-entry' | 'relationship' | 'detail' | 'registers'
  const [activeCardType, setActiveCardType] = useState(null);
  const [codeInput, setCodeInput] = useState('');
  const [counterparty, setCounterparty] = useState(null); // { name, code }
  const [lookupLoading, setLookupLoading] = useState(false);
  const [pickedFromFavourite, setPickedFromFavourite] = useState(false);

  const [allTransactions, setAllTransactions] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [txForm, setTxForm] = useState({
    itemName: '', qty: '', unit: '', amount: '', itemGiven: '', itemReceived: '', isPaid: true, remarks: '',
  });

  const [otpInput, setOtpInput] = useState('');
  const [signatureUrl, setSignatureUrl] = useState('');
  const [signatureWaived, setSignatureWaived] = useState(false);

  const [registerFilters, setRegisterFilters] = useState({ tab: 'Sales' });

  const [businessContacts, setBusinessContacts] = useState([]);
  useEffect(() => {
    fetchContacts('Business').then((res) => setBusinessContacts(res?.data || [])).catch(() => setBusinessContacts([]));
  }, []);

  const loadTransactions = async (filters = {}) => {
    setLoadingList(true);
    try {
      const res = await fetchEDITransactions(filters);
      setAllTransactions(res?.data || []);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadTransactions(); }, []);

  // ================= CARD CLICK -> CODE ENTRY =================
  const openCardType = (type) => {
    setActiveCardType(type);
    setCodeInput('');
    setCounterparty(null);
    setPickedFromFavourite(false);
    setView('code-entry');
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (!codeInput.trim()) return;
    setLookupLoading(true);
    try {
      const res = await lookupBusinessCode(codeInput.trim());
      setCounterparty(res?.data);
      setView('relationship');
    } catch (err) {
      showError(err.message);
    } finally {
      setLookupLoading(false);
    }
  };

  const [pickedFavouriteContact, setPickedFavouriteContact] = useState(null);

  const pickFavouriteBusiness = (contact) => {
    setCodeInput(contact.businessUserCode);
    setPickedFromFavourite(true);
    setPickedFavouriteContact(contact);
  };

  const goWithEntry = () => {
    // Skip the lookup entirely — we already trust a saved favourite's code/name
    setCounterparty({
      name: pickedFavouriteContact?.name || codeInput,
      code: pickedFavouriteContact?.businessUserCode || codeInput,
    });
    setView('relationship');
  };

  // ================= RELATIONSHIP VIEW =================
  const relationshipTransactions = useMemo(() => {
    if (!counterparty) return [];
    return allTransactions
      .filter((t) => t.initiatorCode === counterparty.code || t.counterpartyCode === counterparty.code)
      .filter((t) => viewerType(t, myUserId) === activeCardType)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [allTransactions, counterparty, activeCardType, myUserId]);

  const balance = useMemo(
    () => computeRelationshipBalance(relationshipTransactions, myUserId),
    [relationshipTransactions, myUserId]
  );

  // ================= CREATE / EDIT TRANSACTION =================
  const openCreateForm = () => {
    setEditingTx(null);
    setTxForm({ itemName: '', qty: '', unit: '', amount: '', itemGiven: '', itemReceived: '', isPaid: true, remarks: '' });
    setShowCreateForm(true);
  };

  const openEditForm = (tx) => {
    setEditingTx(tx);
    setTxForm({
      itemName: tx.itemName || '', qty: tx.qty != null ? String(tx.qty) : '', unit: tx.unit || '',
      amount: String(tx.amount || ''), itemGiven: tx.itemGiven || '', itemReceived: tx.itemReceived || '',
      isPaid: tx.isPaid !== false, remarks: tx.remarks || '',
    });
    setShowCreateForm(true);
  };

  const submitTxForm = async (e) => {
    e.preventDefault();
    const payload = {
      transactionType: activeCardType,
      counterpartyCode: counterparty.code,
      ...txForm,
      qty: txForm.qty ? Number(txForm.qty) : undefined,
      amount: txForm.amount ? Number(txForm.amount) : 0,
    };
    try {
      if (editingTx) {
        await updateEDITransaction(editingTx._id, payload);
        showSuccess('Updated — both sides need to re-confirm with new codes.');
      } else {
        await createEDITransaction(payload);
        showSuccess(`${activeCardType} sent to ${counterparty.name} — waiting on both sides to confirm.`);
      }
      await loadTransactions();
      setShowCreateForm(false);
    } catch (err) {
      showError(err.message);
    }
  };

  // ================= TRANSACTION DETAIL =================
  const openDetail = (tx) => {
    setSelectedTx(tx);
    setOtpInput('');
    setSignatureUrl('');
    setSignatureWaived(false);
    setView('detail');
  };

  const isMine = (tx) => String(tx.initiatorUserId?._id || tx.initiatorUserId) === String(myUserId);
  const myOtp = (tx) => (isMine(tx) ? tx.initiatorOtp : tx.counterpartyOtp);
  const iConfirmed = (tx) => (isMine(tx) ? tx.initiatorConfirmed : tx.counterpartyConfirmed);
  const mySignature = (tx) => (isMine(tx) ? tx.initiatorSignature : tx.counterpartySignature);
  const mySignatureWaived = (tx) => (isMine(tx) ? tx.initiatorSignatureWaived : tx.counterpartySignatureWaived);

  const refreshSelectedTx = async (updated) => {
    setSelectedTx(updated);
    await loadTransactions();
  };

  const handleSubmitOtp = async () => {
    if (!otpInput.trim()) return;
    if (!signatureWaived && !signatureUrl) {
      showError('Attach a signature or check "physical signature not required" first.');
      return;
    }
    try {
      const res = await submitOtp(selectedTx._id, otpInput.trim(), signatureUrl, signatureWaived);
      showSuccess(res.message);
      await refreshSelectedTx(res.data);
      setOtpInput('');
    } catch (err) {
      showError(err.response?.data?.message || err.message);
      // reflect the "wrong code" state even on 400 by refetching current doc
      await loadTransactions();
    }
  };

  const handleReject = async () => {
    try {
      const res = await rejectEDITransaction(selectedTx._id, 'Rejected from Local EDI');
      showSuccess('Transaction rejected.');
      await refreshSelectedTx(res.data);
    } catch (err) {
      showError(err.message);
    }
  };

  // ================= REGISTERS =================
  const registerTabs = [
    { key: 'Sales', label: 'Sales Register' },
    { key: 'Purchase', label: 'Purchase Register' },
    { key: 'Barter', label: 'Barter Register' },
    { key: 'CreditSales', label: 'Credit Sales Register' },
    { key: 'CreditPurchase', label: 'Credit Purchase Register' },
  ];

  const registerRows = useMemo(() => {
    const tab = registerFilters.tab;
    let rows = allTransactions.filter((t) => t.status === 'Locked');
    if (tab === 'CreditSales') rows = rows.filter((t) => viewerType(t, myUserId) === 'Sales' && t.isPaid === false);
    else if (tab === 'CreditPurchase') rows = rows.filter((t) => viewerType(t, myUserId) === 'Purchase' && t.isPaid === false);
    else rows = rows.filter((t) => viewerType(t, myUserId) === tab);
    return rows.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [allTransactions, registerFilters, myUserId]);

  const registerTotal = registerRows.reduce((s, t) => s + (t.amount || 0), 0);

  const registerColumns = [
    { header: 'Date', dataKey: 'dateStr' },
    { header: 'Counterparty', dataKey: 'counterpartyName' },
    { header: 'Details', dataKey: 'details' },
    { header: 'Amount', dataKey: 'amountStr' },
  ];
  const registerCsvRows = () => registerRows.map((t) => ({
    dateStr: new Date(t.date).toLocaleDateString(),
    counterpartyName: isMine(t) ? (t.counterpartyUserId?.name || t.counterpartyCode) : (t.initiatorUserId?.name || t.initiatorCode),
    details: t.transactionType === 'Barter' ? `${t.itemGiven} ↔ ${t.itemReceived}` : `${t.itemName}${t.qty ? ` x${t.qty}` : ''}`,
    amountStr: `Rs. ${t.amount}`,
  }));

  const downloadRegisterPdf = () => {
    const label = registerTabs.find((r) => r.key === registerFilters.tab)?.label;
    generateRegisterPDF({ title: label, orientation: ORIENTATION.REGISTER, columns: registerColumns, rows: registerCsvRows(), filename: label });
  };

  const downloadRegisterCsv = () => {
    const label = registerTabs.find((r) => r.key === registerFilters.tab)?.label;
    generateRegisterCSV({ columns: registerColumns, rows: registerCsvRows(), filename: label });
  };

  // ================= RENDER HELPERS =================
  const Header = ({ title, onBack }) => (
    <div className="flex items-center gap-3 mb-6">
      <button onClick={onBack} className="w-fit px-2 py-1 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700">
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Back
      </button>
      <h1 className="text-xl lg:text-2xl font-bold text-cyan-200">{title}</h1>
    </div>
  );

  const TxCard = ({ t }) => {
    const st = STATUS_STYLE[t.status];
    const counterpartyName = isMine(t) ? (t.counterpartyUserId?.name || t.counterpartyCode) : (t.initiatorUserId?.name || t.initiatorCode);
    return (
      <button
        onClick={() => openDetail(t)}
        className="w-full text-left bg-gray-800 border border-gray-700 hover:border-cyan-500/40 rounded-xl p-3 text-xs transition-colors"
      >
        <div className="flex justify-between items-start">
          <div>
            <div className="text-white font-medium">
              {t.transactionType === 'Barter' ? `${t.itemGiven} ↔ ${t.itemReceived}` : `${t.itemName}${t.qty ? ` × ${t.qty}${t.unit ? ' ' + t.unit : ''}` : ''}`}
            </div>
            <div className="text-gray-500 mt-0.5">
              {isMine(t) ? 'To' : 'From'}: {counterpartyName} • {new Date(t.date).toLocaleDateString()}
            </div>
          </div>
          <div className="text-right">
            <div className="font-bold text-white">₹{t.amount}</div>
            <div className={`flex items-center gap-1 justify-end mt-0.5 ${st.color}`}>
              <FontAwesomeIcon icon={st.icon} /> {st.label}
            </div>
          </div>
        </div>
      </button>
    );
  };

  // ================= VIEWS =================
  if (view === 'home') {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-cyan-200 flex items-center gap-3">
                <FontAwesomeIcon icon={faExchangeAlt} /> Local EDI
              </h1>
              <p className="text-xs lg:text-sm text-gray-400">Business-to-business Sales, Purchase &amp; Barter — mutually confirmed, digitally signed</p>
            </div>
            <button onClick={() => navigate('/minierp/home')} className="w-fit px-2 py-1 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-sm">
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Home
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            {CARD_TYPES.map((c) => (
              <button
                key={c.key}
                onClick={() => openCardType(c.key)}
                className="bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-cyan-500/40 rounded-2xl p-6 text-left transition-colors"
              >
                <FontAwesomeIcon icon={c.icon} className={`text-3xl mb-3 text-${c.color}-400`} />
                <div className="text-lg font-semibold text-white">{c.label}</div>
                <div className="text-xs text-gray-500 mt-1">Enter a business code to view or start a deal</div>
              </button>
            ))}
          </div>

          <button
            onClick={() => setView('registers')}
            className="w-full bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-2xl p-4 flex items-center justify-center gap-3 text-cyan-300"
          >
            <FontAwesomeIcon icon={faListUl} /> View Registers
          </button>

          <div className="text-center mt-6">
            <MiniERPMyCodeShare code={myCode} name={authState?.user?.name} className="justify-center" />
          </div>
        </div>
      </div>
    );
  }

  if (view === 'code-entry') {
    const cardMeta = CARD_TYPES.find((c) => c.key === activeCardType);
    return (
      <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
        <div className="max-w-md mx-auto">
          <Header title={cardMeta.label} onBack={() => setView('home')} />
          <MiniERPMyCodeShare code={myCode} name={authState?.user?.name} className="mb-4" />
          <div className="bg-gray-800 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-300">{cardMeta.question}</p>
              <MiniERPContactPicker
                contactType="Business"
                currentCode={codeInput}
                onPick={pickFavouriteBusiness}
              />
            </div>
            <form onSubmit={submitCode} className="space-y-4">
              <input
                type="text"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value.toUpperCase());
                  setPickedFromFavourite(false);
                }}
                placeholder="14-character business code"
                maxLength={14}
                className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-3 font-mono"
                required
              />
              <StatusBanner status={status} />
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={lookupLoading || pickedFromFavourite}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl py-3 font-medium flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faSearch} /> {lookupLoading ? 'Looking up...' : 'Verify'}
                </button>
                {pickedFromFavourite && (
                  <button
                    type="button"
                    onClick={goWithEntry}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 rounded-xl py-3 font-medium flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faCheck} /> Go with Entry
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'relationship') {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
        <div className="max-w-2xl mx-auto">
          <Header title={`${activeCardType} — ${counterparty.name}`} onBack={() => setView('code-entry')} />

          {(balance.theyOweMe > 0 || balance.iOweThem > 0) && (
            <div className="bg-gray-800 rounded-2xl p-4 mb-4 flex flex-wrap gap-4 text-sm">
              {balance.theyOweMe > 0 && <span className="text-emerald-400 font-semibold">They owe you ₹{balance.theyOweMe}</span>}
              {balance.iOweThem > 0 && <span className="text-rose-400 font-semibold">You owe them ₹{balance.iOweThem}</span>}
            </div>
          )}

          {isSubscribed ? (
            <button
              onClick={openCreateForm}
              className="w-full bg-emerald-600 hover:bg-emerald-500 rounded-xl py-3 font-medium flex items-center justify-center gap-2 mb-4"
            >
              <FontAwesomeIcon icon={faPlus} /> New {activeCardType} with {counterparty.name}
            </button>
          ) : !loadingSubs && (
            <div className="w-full bg-gray-800 border border-amber-700 rounded-xl py-3 px-4 text-center text-amber-400 text-sm mb-4">
              An active MiniERP EDI subscription is needed to create new business transactions. You can still view and download existing ones below.
            </div>
          )}

          <div className="space-y-2">
            {loadingList && <p className="text-gray-500 text-sm">Loading...</p>}
            {!loadingList && relationshipTransactions.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-8">No {activeCardType.toLowerCase()} transactions with this business yet.</p>
            )}
            {relationshipTransactions.map((t) => <TxCard key={t._id} t={t} />)}
          </div>
        </div>

        {showCreateForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
              <h2 className="text-xl font-bold text-cyan-200 mb-4">{editingTx ? 'Edit' : 'New'} {activeCardType}</h2>
              <form onSubmit={submitTxForm} className="space-y-4">
                {activeCardType === 'Barter' ? (
                  <>
                    <div>
                      <label className="block text-gray-400 mb-1 text-sm">You Give</label>
                      <input type="text" value={txForm.itemGiven} onChange={(e) => setTxForm({ ...txForm, itemGiven: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" required />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-1 text-sm">You Receive</label>
                      <input type="text" value={txForm.itemReceived} onChange={(e) => setTxForm({ ...txForm, itemReceived: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" required />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-1 text-sm">Estimated Value (₹) <span className="text-gray-500">— record only, not posted to accounts</span></label>
                      <input type="number" value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-gray-400 mb-1 text-sm">Item</label>
                      <input type="text" value={txForm.itemName} onChange={(e) => setTxForm({ ...txForm, itemName: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" required />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <input type="number" placeholder="Qty" value={txForm.qty} onChange={(e) => setTxForm({ ...txForm, qty: e.target.value })} className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                      <input type="text" placeholder="Unit" value={txForm.unit} onChange={(e) => setTxForm({ ...txForm, unit: e.target.value })} className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                      <input type="number" placeholder="Amount ₹" value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" required />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-1 text-sm">Payment Status</label>
                      <select value={txForm.isPaid ? 'Paid' : 'Unpaid'} onChange={(e) => setTxForm({ ...txForm, isPaid: e.target.value === 'Paid' })} className="w-full bg-gray-700 border border-gray-600 rounded-xl px-2 py-1">
                        <option value="Paid">Paid</option>
                        <option value="Unpaid">Unpaid / Credit</option>
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-gray-400 mb-1 text-sm">Remarks</label>
                  <textarea value={txForm.remarks} onChange={(e) => setTxForm({ ...txForm, remarks: e.target.value })} style={{ height: '80px' }} className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                </div>
                <StatusBanner status={status} />
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowCreateForm(false)} className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-2xl hover:bg-gray-700">Cancel</button>
                  <button type="submit" className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-2xl font-medium">{editingTx ? 'Update' : 'Send'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (view === 'detail' && selectedTx) {
    const t = selectedTx;
    const st = STATUS_STYLE[t.status];
    const iAmMine = isMine(t);
    const otherPartyConfirmed = iAmMine ? t.counterpartyConfirmed : t.initiatorConfirmed;

    return (
      <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
        <div className="max-w-lg mx-auto">
          <Header title={`${t.transactionType} Transaction`} onBack={() => setView('relationship')} />

          <div className="bg-gray-800 rounded-2xl p-5 mb-4">
            <div className={`flex items-center gap-2 mb-4 ${st.color}`}>
              <FontAwesomeIcon icon={st.icon} className="text-lg" />
              <span className="font-semibold">{st.label}</span>
              {t.status === 'Locked' && <span className="text-gray-500 text-xs">since {new Date(t.lockedAt).toLocaleString()}</span>}
            </div>

            {t.transactionType === 'Barter' ? (
              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div><div className="text-gray-500">You Give</div><div className="text-white">{t.itemGiven}</div></div>
                <div><div className="text-gray-500">You Receive</div><div className="text-white">{t.itemReceived}</div></div>
              </div>
            ) : (
              <div className="text-sm mb-3">
                <div className="text-gray-500">Item</div>
                <div className="text-white">{t.itemName} {t.qty ? `× ${t.qty} ${t.unit || ''}` : ''}</div>
              </div>
            )}
            <div className="text-sm mb-3">
              <div className="text-gray-500">Amount</div>
              <div className="text-white font-bold">₹{t.amount} {t.isPaid === false && <span className="text-amber-400 font-normal text-xs">(Credit / Unpaid)</span>}</div>
            </div>
            {t.remarks && <div className="text-sm text-gray-400 mb-3">{t.remarks}</div>}

            <div className="text-xs text-gray-500 border-t border-gray-700 pt-3 mt-3">
              {iAmMine ? 'To' : 'From'}: {iAmMine ? (t.counterpartyUserId?.name || t.counterpartyCode) : (t.initiatorUserId?.name || t.initiatorCode)}
            </div>
          </div>

          {t.status === 'PendingApproval' && !iConfirmed(t) && (
            <div className="bg-violet-950/30 border border-violet-700 rounded-2xl p-5 mb-4">
              <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
                <FontAwesomeIcon icon={faLockOpen} /> Confirm &amp; Sign to Lock
              </h3>
              <p className="text-gray-400 text-xs mb-3">
                Call or speak to the other party. Read them <span className="text-white font-semibold">your</span> code below, and enter <span className="text-white font-semibold">their</span> code here — then add your signature or waive it.
              </p>

              <div className="bg-gray-900 rounded-xl p-3 mb-3 text-center">
                <div className="text-gray-500 text-xs">Your code — read this aloud to them</div>
                <div className="text-3xl font-mono font-bold text-cyan-300 tracking-widest">{myOtp(t)}</div>
              </div>

              <input
                type="text"
                value={otpInput}
                onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="Their code"
                className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2 font-mono text-center tracking-widest mb-4"
              />

              <div className="border-t border-gray-700 pt-4 mb-4">
                <h4 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
                  <FontAwesomeIcon icon={faSignature} /> Your Signature
                </h4>

                <label className="flex items-start gap-2 text-xs text-gray-300 mb-3">
                  <input
                    type="checkbox"
                    checked={signatureWaived}
                    onChange={(e) => { setSignatureWaived(e.target.checked); if (e.target.checked) setSignatureUrl(''); }}
                    className="mt-0.5"
                  />
                  This is done online on Think5Whys platform hence physical signature is not required!
                </label>

                {!signatureWaived && (
                  signatureUrl ? (
                    <div className="flex items-center gap-2 text-emerald-400 text-xs">
                      <FontAwesomeIcon icon={faCheckCircle} /> Signature attached
                      <img src={signatureUrl} alt="Your signature" className="h-12 rounded border border-gray-700 ml-2" />
                    </div>
                  ) : (
                    <ImageUploader
                      label=""
                      uploadUrl={`/minierp-edi-signature/${t._id}/${iAmMine ? '1' : '2'}`}
                      token={authState?.token}
                      onChange={(filename) => setSignatureUrl(`/uploads/minierp-edi-signatures/${filename}`)}
                    />
                  )
                )}
              </div>

              <button
                onClick={handleSubmitOtp}
                disabled={!otpInput.trim() || (!signatureWaived && !signatureUrl)}
                className="w-full bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl py-3 font-medium flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faCheck} /> Confirm &amp; Lock My Side
              </button>

              <StatusBanner status={status} className="mt-3" />

              <div className="flex gap-2 mt-4 pt-3 border-t border-gray-700">
                {iAmMine && (
                  <button onClick={() => openEditForm(t)} className="w-fit px-2 py-1 border border-gray-600 rounded-xl hover:bg-gray-700 text-sm flex items-center gap-2">
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </button>
                )}
                <button onClick={handleReject} className="w-fit px-2 py-1 border border-rose-700 text-rose-400 rounded-xl hover:bg-rose-950/40 text-sm flex items-center gap-2">
                  <FontAwesomeIcon icon={faTimesCircle} /> Reject
                </button>
              </div>
            </div>
          )}

          {t.status === 'PendingApproval' && iConfirmed(t) && (
            <div className="bg-gray-800 rounded-2xl p-5 mb-4">
              <div className="flex items-center gap-2 text-emerald-400 text-sm mb-2">
                <FontAwesomeIcon icon={faCheckCircle} /> You've confirmed{mySignatureWaived(t) ? ' (signature waived)' : mySignature(t) ? ' and signed' : ''}. {otherPartyConfirmed ? 'Both confirmed!' : 'Waiting for the other party...'}
              </div>
              {mySignature(t) && <img src={mySignature(t)} alt="Your signature" className="h-16 rounded border border-gray-700" />}
            </div>
          )}

          {t.status === 'Locked' && (
            <div className="bg-gray-800 rounded-2xl p-5 mb-4">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon={faSignature} /> Signatures
              </h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-gray-500 mb-1">Initiator</div>
                  {t.initiatorSignature ? <img src={t.initiatorSignature} alt="Initiator signature" className="h-16 rounded border border-gray-700" /> : (
                    <span className="text-gray-400">{t.initiatorSignatureWaived ? 'Signed digitally via Think5Whys MiniERP' : '-'}</span>
                  )}
                </div>
                <div>
                  <div className="text-gray-500 mb-1">Counterparty</div>
                  {t.counterpartySignature ? <img src={t.counterpartySignature} alt="Counterparty signature" className="h-16 rounded border border-gray-700" /> : (
                    <span className="text-gray-400">{t.counterpartySignatureWaived ? 'Signed digitally via Think5Whys MiniERP' : '-'}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {t.status === 'Locked' && (
            <button onClick={() => generateEDIMemoPDF(t)} className="w-full bg-cyan-600 hover:bg-cyan-500 rounded-xl py-3 font-medium flex items-center justify-center gap-2">
              <FontAwesomeIcon icon={faFilePdf} /> Download Memo PDF
            </button>
          )}
        </div>

        {showCreateForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-3xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
              <h2 className="text-xl font-bold text-cyan-200 mb-4">Edit {activeCardType}</h2>
              <form onSubmit={submitTxForm} className="space-y-4">
                {activeCardType === 'Barter' ? (
                  <>
                    <input type="text" value={txForm.itemGiven} onChange={(e) => setTxForm({ ...txForm, itemGiven: e.target.value })} placeholder="You Give" className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" required />
                    <input type="text" value={txForm.itemReceived} onChange={(e) => setTxForm({ ...txForm, itemReceived: e.target.value })} placeholder="You Receive" className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" required />
                    <input type="number" value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} placeholder="Estimated Value ₹" className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                  </>
                ) : (
                  <>
                    <input type="text" value={txForm.itemName} onChange={(e) => setTxForm({ ...txForm, itemName: e.target.value })} placeholder="Item" className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" required />
                    <div className="grid grid-cols-3 gap-3">
                      <input type="number" placeholder="Qty" value={txForm.qty} onChange={(e) => setTxForm({ ...txForm, qty: e.target.value })} className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                      <input type="text" placeholder="Unit" value={txForm.unit} onChange={(e) => setTxForm({ ...txForm, unit: e.target.value })} className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                      <input type="number" placeholder="Amount ₹" value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} className="bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" required />
                    </div>
                  </>
                )}
                <textarea value={txForm.remarks} onChange={(e) => setTxForm({ ...txForm, remarks: e.target.value })} placeholder="Remarks" style={{ height: '80px' }} className="w-full bg-gray-700 border border-gray-600 rounded-xl px-3 py-2" />
                <StatusBanner status={status} />
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowCreateForm(false)} className="flex-1 w-fit px-2 py-1 border border-gray-600 rounded-2xl hover:bg-gray-700">Cancel</button>
                  <button type="submit" className="flex-1 w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-2xl font-medium">Update</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (view === 'registers') {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-200 p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <Header title="EDI Registers" onBack={() => setView('home')} />

          <div className="flex flex-wrap gap-2 mb-5">
            {registerTabs.map((r) => (
              <button
                key={r.key}
                onClick={() => setRegisterFilters({ tab: r.key })}
                className={`px-4 py-2 rounded-xl text-sm w-fit ${registerFilters.tab === r.key ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="bg-gray-800 rounded-2xl p-5">
            <div className="flex justify-between items-center mb-4">
              <span className="text-lg font-bold text-white">Total: ₹{registerTotal.toFixed(2)}</span>
              <div className="flex gap-2">
                <button onClick={downloadRegisterPdf} className="w-fit px-2 py-1 bg-cyan-600 hover:bg-cyan-500 rounded-xl text-sm flex items-center gap-2">
                  <FontAwesomeIcon icon={faFilePdf} /> PDF
                </button>
                {isSubscribed && (
                  <button onClick={downloadRegisterCsv} className="w-fit px-2 py-1 bg-emerald-600 hover:bg-emerald-500 rounded-xl text-sm flex items-center gap-2">
                    <FontAwesomeIcon icon={faFileCsv} /> CSV
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {registerRows.length === 0 && <p className="text-gray-500 text-sm">No entries yet.</p>}
              {registerRows.map((t) => <TxCard key={t._id} t={t} />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MiniERPLocalEDI;
